import {
  CSSProperties,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from "react";
import type { DragEndEvent, UniqueIdentifier } from "@dnd-kit/core";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS as dndCSS } from "@dnd-kit/utilities";
import { Control, useFieldArray } from "react-hook-form";
import type { UpdateFeaturedItemInput } from "common/utils/featuredContent";
import { useConfirmDestructiveAction } from "../../../../hooks/useConfirmDestructiveAction";
import { colors, spacing } from "../../../../stitches.config";
import { FlexColumn, FlexRow } from "../../../Common/__styles__/Layout";
import { Button } from "../../../Common/Buttons/Button";
import { Icon } from "../../../Common/Icons/LucideIcons";
import { SettingsSection } from "../../../Common/Settings";
import { Body } from "../../../Common/Typography";
import { FEATURED_ITEM_MAX_WIDTH } from "../../../FeaturedContent/__styles__/FeaturedItem";
import { FeaturedItem } from "../../../FeaturedContent/FeaturedItem";
import DropdownMenu, { ActionsProps } from "../../../Inputs/DropdownMenu";
import type { FormStructure } from "../PublicWebsiteSettingsForm";
import { AddContentButtonContainer } from "./__styles__/FeaturedContentSectionsInput";
import {
  FEATURED_CONTENT_REDUCER_ACTION_TYPE,
  featuredContentReducer,
  FeaturedContentSectionWithImage,
  FeaturedItemWithImage,
} from "./featuredContentInputReducer";
import { useAddFeaturedItemModal } from "./FeaturedItemModal";
import { useAddSectionModal, useEditSectionModal } from "./SectionTitleModal";
import { convertItemToFeaturedItemProps, getFeaturedItemId } from "./utils";

const GRID_COLUMNS = 3;

export type FeaturedItemInput = UpdateFeaturedItemInput & {
  imageMimeType?: string;
  imageBlobUrl?: string;
};

type SectionActionMenuProps = {
  section: FeaturedContentSectionWithImage;
  sectionIndex: number;
  onUpdate: (sectionId: string, title: string) => void;
  onDelete: (sectionId: string) => void;
};

const SectionActionMenu = ({
  section,
  sectionIndex,
  onUpdate,
  onDelete,
}: SectionActionMenuProps) => {
  const [showEditSectionModal] = useEditSectionModal({
    onUpdate: (title: string) => {
      const sectionId =
        section.id || section.reactHookFormId || `section-${sectionIndex}`;
      onUpdate(sectionId, title);
    },
    initialTitle: section.title,
  });

  const sectionId = section.id || section.reactHookFormId!;

  const showDeleteConfirmation = useConfirmDestructiveAction({
    title: "Delete section",
    subtitle: section.title,
    content: (
      <Body size="large" type="regular">
        All featured content in this section will be deleted.{" "}
        <Body size="large" type="emphasis" as="span">
          This action cannot be undone.
        </Body>
      </Body>
    ),
    onConfirm: () => {
      onDelete(sectionId);
    },
  });

  const actions: Array<ActionsProps> = [
    {
      label: "Edit section",
      onClick: () => {
        showEditSectionModal();
      },
    },
    {
      label: "Delete section",
      variant: "red",
      onClick: () => {
        showDeleteConfirmation();
      },
    },
  ];
  return <DropdownMenu actions={actions} isTableAction />;
};

type SortableSectionProps = {
  section: FeaturedContentSectionWithImage;
  sectionIndex: number;
  onUpdateSection: (sectionId: string, title: string) => void;
  onDeleteSection: (sectionId: string) => void;
  onAddFeaturedItem: (sectionId: string) => void;
  onUpdateFeaturedItem: (
    sectionId: string,
    itemId: string,
    data: Omit<UpdateFeaturedItemInput, "displayOrder"> & {
      imageMimeType?: string;
      imageBlobUrl?: string;
    },
  ) => void;
  onDeleteFeaturedItem: (sectionId: string, itemId: string) => void;
  onReorderItem: (sectionId: string, activeId: string, overId: string) => void;
  dragHandleProps: Pick<
    ReturnType<typeof useSortable>,
    "attributes" | "listeners"
  >;
};

const SortableSection = ({
  section,
  sectionIndex,
  onUpdateSection,
  onDeleteSection,
  onAddFeaturedItem,
  onUpdateFeaturedItem,
  onDeleteFeaturedItem,
  onReorderItem,
  dragHandleProps,
}: SortableSectionProps) => {
  const sectionId = section.id || section.reactHookFormId;

  const sectionItems = useMemo(
    () =>
      section.featuredItems
        ?.filter(item => !item.destroy)
        .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)) || [],
    [section.featuredItems],
  );

  const itemIds = useMemo<UniqueIdentifier[]>(
    () => sectionItems.map(item => item.id || `item-${item.displayOrder}`),
    [sectionItems],
  );

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {}),
  );

  if (!sectionId) {
    return null;
  }

  const handleItemDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      onReorderItem(sectionId, String(active.id), String(over.id));
    }
  };

  const itemsInLastRow = sectionItems.length % GRID_COLUMNS;
  const shouldRenderFlattened =
    (itemsInLastRow === 0 && sectionItems.length > 0) ||
    sectionItems.length === 0;
  const shouldAddButtonInGrid = !shouldRenderFlattened;
  const gridColumnSpan =
    shouldAddButtonInGrid ?
      itemsInLastRow === 0 ?
        GRID_COLUMNS
      : 1
    : 0;

  const gridChildren: ReactNode[] = [
    ...sectionItems.map(item => {
      const itemId = item.id || `item-${item.displayOrder}`;
      const displayItem = convertItemToFeaturedItemProps(item);
      return (
        <SortableItem
          key={itemId}
          id={itemId}
          item={item}
          displayItem={displayItem}
          sectionId={sectionId}
          onUpdate={onUpdateFeaturedItem}
          onDelete={onDeleteFeaturedItem}
        />
      );
    }),
    ...(shouldAddButtonInGrid ?
      [
        <div
          key={`add-content-${sectionId}`}
          style={{
            gridColumn: `span ${gridColumnSpan}`,
            width: "100%",
            minWidth: 0,
          }}
        >
          <AddContentButton
            onClick={() => onAddFeaturedItem(sectionId)}
            matchImageHeight={itemsInLastRow > 0}
          />
        </div>,
      ]
    : []),
  ];

  return (
    <DndContext
      collisionDetection={closestCenter}
      onDragEnd={handleItemDragEnd}
      sensors={sensors}
    >
      <SortableContext items={itemIds} strategy={verticalListSortingStrategy}>
        <FlexColumn gap="m" style={{ width: "100%" }}>
          <FlexRow
            style={{
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
            }}
          >
            <div
              {...dragHandleProps.attributes}
              {...dragHandleProps.listeners}
              style={{
                cursor: "grab",
                paddingLeft: spacing.l.value,
                display: "flex",
                alignItems: "center",
              }}
            >
              <Icon
                iconName="grip-vertical"
                size={16}
                color="contentSecondary"
                style={{ marginLeft: `-${spacing.l.value}` }}
              />
            </div>
            <FlexRow
              gap="s"
              style={{
                alignItems: "center",
                flex: 1,
                minWidth: 0,
                paddingLeft: spacing.s.value,
              }}
            >
              <Body
                size="default"
                type="emphasis"
                style={{ whiteSpace: "nowrap" }}
              >
                {section.title}
              </Body>
            </FlexRow>
            <div style={{ paddingRight: spacing.m.value }}>
              <SectionActionMenu
                section={section}
                sectionIndex={sectionIndex}
                onUpdate={onUpdateSection}
                onDelete={onDeleteSection}
              />
            </div>
          </FlexRow>

          <div
            style={{
              paddingLeft: spacing.l.value,
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(${GRID_COLUMNS}, minmax(0, ${FEATURED_ITEM_MAX_WIDTH}))`,
                gap: spacing.l.value,
                width: "100%",
                alignItems: "start",
                justifyItems: "start",
              }}
            >
              {gridChildren}
            </div>
            {shouldRenderFlattened && (
              <div style={{ marginTop: spacing.l.value }}>
                <AddContentButton
                  flattened={sectionItems.length > 0}
                  onClick={() => onAddFeaturedItem(sectionId)}
                />
              </div>
            )}
          </div>
        </FlexColumn>
      </SortableContext>
    </DndContext>
  );
};

type SortableItemProps = {
  id: string;
  item: FeaturedItemWithImage;
  displayItem: ReturnType<typeof convertItemToFeaturedItemProps>;
  sectionId: string;
  onUpdate: (
    sectionId: string,
    itemId: string,
    data: Omit<UpdateFeaturedItemInput, "displayOrder"> & {
      imageMimeType?: string;
      imageBlobUrl?: string;
    },
  ) => void;
  onDelete: (sectionId: string, itemId: string) => void;
};

const SortableItem = ({
  id,
  item,
  displayItem,
  sectionId,
  onUpdate,
  onDelete,
}: SortableItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: CSSProperties = {
    transform: dndCSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: "relative",
    cursor: isDragging ? "grabbing" : "grab",
  };

  const editProps = useMemo(
    () => ({
      item: {
        ...item,
        id: item.id ?? undefined,
        displayOrder: item.displayOrder ?? 0,
      },
      sectionId,
      onUpdate,
      onDelete,
    }),
    [item, sectionId, onUpdate, onDelete],
  );

  return (
    <div
      ref={setNodeRef}
      style={{
        ...style,
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        margin: 0,
        padding: 0,
        boxSizing: "border-box",
      }}
      {...attributes}
      {...listeners}
    >
      <FeaturedItem
        image={displayItem.image}
        title={displayItem.title}
        description={displayItem.description}
        link={displayItem.link}
        entityType={displayItem.entityType}
        entityId={displayItem.entityId}
        editProps={editProps}
      />
    </div>
  );
};

const AddContentButton = ({
  onClick,
  flattened = false,
  matchImageHeight = false,
}: {
  onClick: () => void;
  flattened?: boolean;
  matchImageHeight?: boolean;
}) => {
  return (
    <AddContentButtonContainer
      flattened={flattened}
      matchImageHeight={matchImageHeight}
      onClick={onClick}
    >
      <Icon iconName="plus" size={16} color="contentInfo" />
      <Body size="small" type="emphasis" color="contentInfo">
        Add content
      </Body>
    </AddContentButtonContainer>
  );
};

type FeaturedContentSectionsInputProps = {
  control: Control<FormStructure>;
};

export const FeaturedContentSectionsInput = ({
  control,
}: FeaturedContentSectionsInputProps) => {
  const { fields, replace } = useFieldArray({
    control,
    name: "featuredContent",
    keyName: "reactHookFormId",
  });

  const [state, dispatch] = useReducer(featuredContentReducer, fields);

  useEffect(() => {
    dispatch({
      type: FEATURED_CONTENT_REDUCER_ACTION_TYPE.INITIALIZE,
      payload: fields,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    replace(state);
  }, [state, replace]);

  const [showAddSectionModal] = useAddSectionModal({
    onAdd: (title: string) => {
      dispatch({
        type: FEATURED_CONTENT_REDUCER_ACTION_TYPE.ADD_SECTION,
        payload: { title },
      });
    },
  });

  const currentSectionIdRef = useRef<Maybe<string>>(null);

  const getImageUrl = (
    imageFilename: string,
    imageBlobUrl?: string,
  ): string => {
    if (imageBlobUrl) {
      return imageBlobUrl;
    }
    return `/${imageFilename}`;
  };

  const handleAddFeaturedItem = (
    sectionId: string,
    data: FeaturedItemInput,
  ) => {
    const imageUrl = getImageUrl(data.imageFilename, data.imageBlobUrl);

    const { imageBlobUrl: _, ...itemData } = data;

    dispatch({
      type: FEATURED_CONTENT_REDUCER_ACTION_TYPE.ADD_FEATURED_ITEM,
      payload: {
        sectionId,
        item: {
          ...itemData,
          imageMimeType: itemData.imageMimeType,
          image: {
            url: imageUrl,
            accessibilityText: itemData.imageAccessibilityText,
          },
        },
      },
    });
  };

  const handleUpdateFeaturedItem = useCallback(
    (
      sectionId: string,
      itemId: string,
      data: Omit<UpdateFeaturedItemInput, "displayOrder"> & {
        imageMimeType?: string;
        imageBlobUrl?: string;
      },
    ) => {
      const section = state.find(s => {
        const sId = s.id || s.reactHookFormId;
        return sId === sectionId;
      });

      const existingItem = section?.featuredItems?.find(item => {
        return getFeaturedItemId(item) === itemId;
      });

      const imageUrl =
        data.imageBlobUrl ??
        existingItem?.image.url ??
        getImageUrl(data.imageFilename);

      const { imageBlobUrl: _, ...itemData } = data;

      dispatch({
        type: FEATURED_CONTENT_REDUCER_ACTION_TYPE.UPDATE_FEATURED_ITEM,
        payload: {
          sectionId,
          itemId,
          item: {
            ...itemData,
            displayOrder: existingItem?.displayOrder ?? 0,
            imageMimeType: itemData.imageMimeType,
            image: {
              url: imageUrl,
              accessibilityText: itemData.imageAccessibilityText,
            },
          },
        },
      });
    },
    [dispatch, state],
  );

  const handleDeleteFeaturedItem = useCallback(
    (sectionId: string, itemId: string) => {
      dispatch({
        type: FEATURED_CONTENT_REDUCER_ACTION_TYPE.DELETE_FEATURED_ITEM,
        payload: {
          sectionId,
          itemId,
        },
      });
    },
    [dispatch],
  );

  const handleReorderSection = (activeId: string, overId: string) => {
    dispatch({
      type: FEATURED_CONTENT_REDUCER_ACTION_TYPE.REORDER_SECTION,
      payload: {
        activeId,
        overId,
      },
    });
  };

  const handleReorderFeaturedItem = (
    sectionId: string,
    activeId: string,
    overId: string,
  ) => {
    dispatch({
      type: FEATURED_CONTENT_REDUCER_ACTION_TYPE.REORDER_FEATURED_ITEM,
      payload: {
        sectionId,
        activeId,
        overId,
      },
    });
  };

  const handleUpdateSection = (sectionId: string, title: string) => {
    dispatch({
      type: FEATURED_CONTENT_REDUCER_ACTION_TYPE.UPDATE_SECTION,
      payload: {
        sectionId,
        title,
      },
    });
  };

  const handleDeleteSection = (sectionId: string) => {
    dispatch({
      type: FEATURED_CONTENT_REDUCER_ACTION_TYPE.DELETE_SECTION,
      payload: {
        sectionId,
      },
    });
  };

  const [showAddFeaturedItemModal] = useAddFeaturedItemModal({
    onAdd: (data: FeaturedItemInput) => {
      if (!currentSectionIdRef.current) {
        return;
      }

      const sectionId = currentSectionIdRef.current;
      handleAddFeaturedItem(sectionId, data);
      currentSectionIdRef.current = null;
    },
  });

  const filteredSections = state.filter(section => !section.destroy);
  const sectionIds = useMemo<UniqueIdentifier[]>(
    () =>
      filteredSections.map(
        section => section.id || section.reactHookFormId || "",
      ),
    [filteredSections],
  );

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {}),
  );

  const handleSectionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      handleReorderSection(String(active.id), String(over.id));
    }
  };

  return (
    <SettingsSection
      title="Homepage highlights"
      subtitle="This will show up as featured content on the public website homepage."
      fullWidth
      actionButton={
        <div
          style={{
            height: spacing.l.toString(),
            display: "flex",
            alignItems: "center",
          }}
        >
          <Button
            variant="outline"
            size="small"
            title="Add section"
            iconStart="plus"
            onPress={showAddSectionModal}
          />
        </div>
      }
    >
      <DndContext
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
        onDragEnd={handleSectionDragEnd}
        sensors={sensors}
      >
        <SortableContext
          items={sectionIds}
          strategy={verticalListSortingStrategy}
        >
          <FlexColumn gap="l" style={{ width: "100%" }}>
            {filteredSections.map((section, sectionIndex) => {
              return (
                <SortableSectionWrapper
                  key={section.id || section.reactHookFormId}
                  section={section}
                  sectionIndex={sectionIndex}
                  onUpdateSection={handleUpdateSection}
                  onDeleteSection={handleDeleteSection}
                  onAddFeaturedItem={(sectionId: string) => {
                    currentSectionIdRef.current = sectionId;
                    showAddFeaturedItemModal();
                  }}
                  onUpdateFeaturedItem={handleUpdateFeaturedItem}
                  onDeleteFeaturedItem={handleDeleteFeaturedItem}
                  onReorderItem={handleReorderFeaturedItem}
                />
              );
            })}
          </FlexColumn>
        </SortableContext>
      </DndContext>
    </SettingsSection>
  );
};

type SortableSectionWrapperProps = {
  section: FeaturedContentSectionWithImage;
  sectionIndex: number;
  onUpdateSection: (sectionId: string, title: string) => void;
  onDeleteSection: (sectionId: string) => void;
  onAddFeaturedItem: (sectionId: string) => void;
  onUpdateFeaturedItem: (
    sectionId: string,
    itemId: string,
    data: Omit<UpdateFeaturedItemInput, "displayOrder"> & {
      imageMimeType?: string;
      imageBlobUrl?: string;
    },
  ) => void;
  onDeleteFeaturedItem: (sectionId: string, itemId: string) => void;
  onReorderItem: (sectionId: string, activeId: string, overId: string) => void;
};

const SortableSectionWrapper = ({
  section,
  sectionIndex,
  onUpdateSection,
  onDeleteSection,
  onAddFeaturedItem,
  onUpdateFeaturedItem,
  onDeleteFeaturedItem,
  onReorderItem,
}: SortableSectionWrapperProps) => {
  const sectionId = section.id || section.reactHookFormId;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: sectionId || "" });

  if (!sectionId) {
    return null;
  }

  const style: CSSProperties = {
    transform: dndCSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: "relative",
    backgroundColor:
      isDragging ? colors.bgUiContainer.toString() : "transparent",
    borderRadius: "4px",
  };

  return (
    <div ref={setNodeRef} style={style}>
      <SortableSection
        section={section}
        sectionIndex={sectionIndex}
        onUpdateSection={onUpdateSection}
        onDeleteSection={onDeleteSection}
        onAddFeaturedItem={onAddFeaturedItem}
        onUpdateFeaturedItem={onUpdateFeaturedItem}
        onDeleteFeaturedItem={onDeleteFeaturedItem}
        onReorderItem={onReorderItem}
        dragHandleProps={{ attributes, listeners }}
      />
    </div>
  );
};
